"use client"

// components/tracker/drug-quick-view.tsx
//
// The drug quick view drawer (Paper Q61-0, FE-DRUG-PAGE-1 Phase 4a-ii): a
// 600-wide panel at the right edge over a viewport-wide backdrop, opened
// from a card's "Open drug". It fetches `/api/v2/drugs/[id]` on open and
// shows a skeleton until the detail lands; a failed read branches on the
// envelope's `code`, never its `message`.
//
// The eight blocks of spec §4 in order: header, identity, stat trio,
// identity & target, licensed uses, conditions being studied, follow the
// evidence, and the primary action -- which is HIDDEN until Phase 4b lands
// the record page at /tools/trial-tracker/drugs/[id] (the plan's choice:
// hidden, not a dead link). "View evidence" in block 5 points at the same
// page and is hidden for the same reason.
//
// Copy decisions from the plan: `Owner` is `Holder` (the most frequent
// licence holder, "not recorded" when none); the target line says "Target
// (Open Targets)" and never "mechanism", so block 4's heading is "Identity
// & target"; the licence caveat shows whenever any licence was matched by
// name (`linkedBy === "inn"`) or the label read failed; a "+N more" on the
// conditions uses `conditionsTotalDistinct`; empty rows read in the muted
// mono register ("No target named", "No indication on record").
//
// Accessibility: `role="dialog"`, `aria-modal`, focus moves to the close
// control on open and returns to the opener on close (the opener comes in
// as a prop from the click's `currentTarget`), Tab cycles inside the panel,
// Escape and the scrim close, and the page behind cannot scroll while the
// drawer holds the floor. THE SCROLL LOCK IS TWO LOCKS: `body.overflow`
// (the track-preview-drawer lock) AND the nearest scrolling ancestor of the
// panel, because under the shell's `fullHeightCanvas` the page scrolls in an
// inner `overflow-y-auto` div, not the body, and a wheel over the scrim was
// scrolling the cards behind (4a-ii review fix 3).

import { useEffect, useMemo, useRef, useState } from "react"

import { displayAliasText } from "@/components/tracker/drug-card"
import { useDrugAvatar } from "@/components/tracker/drug-identity-context"
import { ExpiredUpgradeCard } from "@/components/tracker/upgrade-rail"
import { classAxisRank } from "@/lib/drugs/class-axes"
import type { DrugCard } from "@/lib/drugs/load-drugs-overview"
import type {
  DrugClass,
  DrugDetail,
  DrugLicence,
} from "@/lib/drugs/load-drug-detail"

import styles from "@/components/tracker/drug-quick-view.module.css"

/** Q61-0 draws three rows; the rest sit behind "+N more". */
const CONDITIONS_SHOWN = 3

/** The caveat, verbatim with its drawn line break (QCY-0). */
const LICENCE_CAVEAT =
  "Name-based licence matches. Confirm the source label,\npopulation and jurisdiction for each licensed use."

/** Block 7 (QDE-0): one text node with two newlines, as drawn. */
const FOLLOW_BODY =
  "Trial changes & results →\nLicence holders & related companies →\nSource records & accepted names →"

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: DrugDetail }
  /** The route withheld the detail (`gated: true`): the member's free week ended. */
  | { status: "gated" }
  | { status: "error"; code: string }

const num = (n: number) => n.toLocaleString("en-GB")

/** The most frequent `holder` across the licences, ties by first seen. */
export function mostFrequentHolder(licences: DrugLicence[]): string | null {
  const counts = new Map<string, number>()
  for (const l of licences) {
    const h = l.holder?.trim()
    if (!h) continue
    counts.set(h, (counts.get(h) ?? 0) + 1)
  }
  let best: string | null = null
  let bestN = 0
  for (const [h, n] of counts) {
    if (n > bestN) {
      best = h
      bestN = n
    }
  }
  return best
}

/**
 * The "Licence matches" figure: DISTINCT `(jurisdiction, application_ref)`
 * pairs, the same definition as the card's `licenceCount` (the overview's
 * `licenceCountFor`), so the two never disagree on screen (review fix).
 */
export function licencePairCount(licences: DrugLicence[]): number {
  const pairs = new Set<string>()
  for (const l of licences) pairs.add(`${l.jurisdiction}\u0000${l.applicationRef}`)
  return pairs.size
}

/**
 * The nearest ancestor that scrolls vertically (computed `overflow-y` auto
 * or scroll), or null when only the viewport does. Walks from the panel
 * itself, so the panel's own scroller is skipped by starting at its parent.
 */
function nearestScroller(from: HTMLElement | null): HTMLElement | null {
  let el = from?.parentElement ?? null
  while (el && el !== document.body) {
    const oy = window.getComputedStyle(el).overflowY
    if (oy === "auto" || oy === "scroll") return el
    el = el.parentElement
  }
  return null
}

/** Distinct licensed uses across every licence text, first seen first. */
function licensedUses(licences: DrugLicence[]): { meshId: string; name: string }[] {
  const seen = new Map<string, string>()
  for (const l of licences) {
    for (const ind of l.indications) {
      for (const use of ind.uses) {
        if (!seen.has(use.meshId)) seen.set(use.meshId, use.name)
      }
    }
  }
  return [...seen].map(([meshId, name]) => ({ meshId, name }))
}

/** Action as stored ("INHIBITOR") to the drawn form ("Inhibitor"). */
function actionWord(action: string | null): string | null {
  if (!action) return null
  const a = action.trim().toLowerCase().replace(/_/g, " ")
  return a ? a.charAt(0).toUpperCase() + a.slice(1) : null
}

/**
 * A two-part badge for the Identity block (founder 2026-09-07, after the
 * filter-chip reference): a muted source label, a hairline divider (the
 * value's left border), then the value in ink weight 500. `source` is the
 * attribution, shown twice on purpose: as a tiny muted suffix inside the
 * label so it reads without hovering, and as the badge's `title` tooltip.
 * A badge with no label is a bare value pill (the "+N more" trailer and
 * the empty states), muted.
 */
function SourceBadge({
  label,
  source,
  value,
  extra,
  muted = false,
}: {
  label?: string
  source?: string
  value: string
  /** A muted trailer ("+N more") that stays inside the value cell. */
  extra?: string
  muted?: boolean
}) {
  return (
    <span className={styles.badge} title={source}>
      {label ? (
        <span className={styles.badgeLabel}>
          {label}
          {source ? <span className={styles.badgeSource}>{source}</span> : null}
        </span>
      ) : null}
      <span
        className={`${styles.badgeValue}${label ? "" : ` ${styles.badgeValueOnly}`}${muted ? ` ${styles.badgeValueMuted}` : ""}`}
      >
        {value}
        {extra ? <span className={styles.badgeExtra}>{extra}</span> : null}
      </span>
    </span>
  )
}

/**
 * `CLASS_AXIS_LABELS` reads "Mechanism (Open Targets)": the badge wants the
 * label and the source apart. The bracket is the source; "EMA" spells out to
 * the report it names, and the atc label shortens to "ATC group" (founder
 * wording). A label with no bracket is its own label, unattributed.
 */
function splitAxisLabel(axis: string, axisLabel: string): { label: string; source?: string } {
  const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(axisLabel)
  if (!m) return { label: axisLabel }
  const source = m[2] === "EMA" ? "EMA medicines report" : m[2]
  const label = axis === "atc" ? "ATC group" : m[1]
  return { label, source }
}

/** Badge label per identifier scheme; unknown schemes print the scheme as read. */
const IDENTIFIER_LABELS: Readonly<Record<string, string>> = {
  mesh: "MeSH",
  unii: "UNII",
  rxnorm: "RxNorm",
  chembl: "ChEMBL",
}
const IDENTIFIER_ORDER = ["mesh", "unii", "rxnorm", "chembl"]

/**
 * A RENDER PROJECTION, not a data change: a holder stored all upper case
 * ("ASTRAZENECA AB") prints in title case ("Astrazeneca Ab"). Mixed-case
 * holders pass through untouched, so a deliberately cased name is kept.
 */
function holderDisplay(holder: string): string {
  if (holder !== holder.toUpperCase() || holder === holder.toLowerCase()) return holder
  return holder
    .toLowerCase()
    .replace(/(^|[\s\-/(])(\p{L})/gu, (_m, pre: string, ch: string) => pre + ch.toUpperCase())
}

function errorCopy(code: string): string {
  switch (code) {
    case "not_found":
      return "This drug record could not be found."
    case "rate_limited":
      return "Too many requests. Wait a minute and open the drug again."
    case "unauthorized":
    case "forbidden":
      return "Sign in again to open this drug."
    default:
      return "The drug record did not come back. Close and try again."
  }
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function DrugQuickView({
  card,
  watchName,
  opener,
  onClose,
}: {
  /** The card that opened the drawer; its `name` is the avatar key. */
  card: DrugCard
  /** The watch's display name for "Tracking in <watch>". */
  watchName: string
  /**
   * The control that opened the drawer (the click's `currentTarget`), which
   * takes focus back on close. Falls back to whatever had focus on mount.
   */
  opener?: HTMLElement | null
  onClose: () => void
}) {
  const avatar = useDrugAvatar(card.name)
  const assetId = card.assetId
  const [state, setState] = useState<LoadState>({ status: "loading" })
  const [showAllConditions, setShowAllConditions] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  // The detail read, one per open. A stale response cannot land after the
  // drawer moved on: the abort covers the request, the flag the parse.
  useEffect(() => {
    if (!assetId) return
    const ac = new AbortController()
    let live = true
    setState({ status: "loading" })
    setShowAllConditions(false)
    const run = async () => {
      try {
        const res = await fetch(`/api/v2/drugs/${assetId}`, {
          headers: { Accept: "application/json" },
          signal: ac.signal,
        })
        if (!res.ok) {
          let code = `http_${res.status}`
          try {
            const body = (await res.json()) as { error?: { code?: string } }
            if (body?.error?.code) code = body.error.code
          } catch {
            // No envelope: keep the status code.
          }
          if (live) setState({ status: "error", code })
          return
        }
        const json = (await res.json()) as DrugDetail & { gated?: boolean }
        // The W3 trial gate (Phase GATE): the route answers 200 with
        // `gated: true` and no detail, the Changes route's posture.
        if (json.gated === true) {
          if (live) setState({ status: "gated" })
          return
        }
        if (live) setState({ status: "ready", detail: json })
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return
        if (live) setState({ status: "error", code: "network" })
      }
    }
    void run()
    return () => {
      live = false
      ac.abort()
    }
  }, [assetId])

  // Focus in on open, back to the opener on close. The opener is the prop
  // (from the click's `currentTarget`; Safari does not focus a clicked
  // button), falling back to whatever had focus when the drawer mounted.
  // Captured once, on mount, by design.
  const openerRef = useRef<HTMLElement | null>(opener ?? null)
  useEffect(() => {
    const target =
      openerRef.current ?? (document.activeElement as HTMLElement | null)
    closeRef.current?.focus()
    return () => {
      target?.focus?.()
    }
  }, [])

  // The page behind the drawer must not scroll while it holds the floor:
  // lock the body (the track-preview-drawer lock) AND the nearest scrolling
  // ancestor of the panel, which under `fullHeightCanvas` is the shell's
  // inner `overflow-y-auto` div. Both restored on close and on unmount.
  useEffect(() => {
    const prevBody = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const scroller = nearestScroller(panelRef.current)
    const prevScroller = scroller ? scroller.style.overflow : null
    if (scroller) scroller.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prevBody
      if (scroller && prevScroller !== null) scroller.style.overflow = prevScroller
    }
  }, [])

  // Escape closes and Tab cycles inside the panel, both on the DOCUMENT (the
  // TrialDetailDrawer's window listener for Escape): a handler on the
  // overlay only sees keys from elements inside it, and the first live run
  // showed a Tab that had escaped to the rail leaving Escape dead. The
  // `focusin` guard is the belt to that brace: whatever moves focus outside
  // the panel, it comes straight back.
  useEffect(() => {
    const focusables = () =>
      panelRef.current
        ? Array.from(
            panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
          ).filter((n) => n.offsetParent !== null)
        : []
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== "Tab" || !panelRef.current) return
      const nodes = focusables()
      if (nodes.length === 0) {
        e.preventDefault()
        panelRef.current.focus()
        return
      }
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      const inside = panelRef.current.contains(active)
      if (!inside) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
      } else if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    const onFocusIn = (e: FocusEvent) => {
      const panel = panelRef.current
      // A detached panel is one being unmounted: the opener is taking focus
      // back, and the guard must not fight it.
      if (!panel || !panel.isConnected || panel.contains(e.target as Node)) return
      const nodes = focusables()
      ;(nodes[0] ?? panel).focus()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("focusin", onFocusIn)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("focusin", onFocusIn)
    }
  }, [onClose])

  const detail = state.status === "ready" ? state.detail : null

  // The composer's `displayAliases` is `rankAliases` over the same rows the
  // card used, so card and drawer print one line (review fix 2).
  const aliasLine = useMemo(() => {
    if (!detail) return null
    if (detail.identity === null) return "Names not loaded"
    const top = detail.identity.displayAliases.map(displayAliasText)
    return top.length ? top.join(" · ") : "No brand or code on record"
  }, [detail])

  // One badge row per axis, at most, in display order (epc, moa, pa, mech,
  // cs, atc). Within an axis the first class in `classes[]` order prints (the
  // composer sorts each axis highest confidence first, then class_norm, so the
  // pick is never a random uuid's read order); the rest sit behind a muted
  // "+N more" badge at the row's end (regorafenib carries 15 `mech` classes,
  // which used to print as 15 rows). The composer has already folded one
  // `class_norm` on two axes into one entry, so a class never prints twice.
  // Every axis is labelled now, the first included (BE-CLASS-1 Phase 5
  // badges); the source rides on the label, not as a sentence suffix.
  const classRows = useMemo(() => {
    if (!detail || detail.classes === null) return []
    const byAxis = new Map<string, { axis: string; first: DrugClass; extra: number }>()
    for (const c of detail.classes) {
      const row = byAxis.get(c.axis)
      if (row) row.extra += 1
      else byAxis.set(c.axis, { axis: c.axis, first: c, extra: 0 })
    }
    return [...byAxis.values()].sort((a, b) => classAxisRank(a.axis) - classAxisRank(b.axis))
  }, [detail])

  // One badge per identifier, known schemes first in a fixed order, the rest
  // as read. Null when the read failed, empty when nothing is recorded.
  const identifierBadges = useMemo(() => {
    if (!detail || detail.identifiers === null) return null
    const rank = (scheme: string) => {
      const i = IDENTIFIER_ORDER.indexOf(scheme.toLowerCase())
      return i === -1 ? IDENTIFIER_ORDER.length : i
    }
    return [...detail.identifiers]
      .sort((a, b) => rank(a.scheme) - rank(b.scheme))
      .map((i) => ({
        key: `${i.scheme}:${i.identifier}`,
        label: IDENTIFIER_LABELS[i.scheme.toLowerCase()] ?? i.scheme,
        value: i.identifier,
      }))
  }, [detail])

  const uses = useMemo(
    () => (detail?.licences ? licensedUses(detail.licences) : null),
    [detail],
  )
  const holder = useMemo(
    () => (detail?.licences ? mostFrequentHolder(detail.licences) : null),
    [detail],
  )
  const showCaveat =
    !!detail &&
    (detail.licencesLabelReadFailed ||
      (detail.licences?.some((l) => l.linkedBy === "inn") ?? false))

  const conditions = detail?.conditionsStudied ?? null
  const conditionsShown = conditions
    ? showAllConditions
      ? conditions
      : conditions.slice(0, CONDITIONS_SHOWN)
    : []
  const moreConditions =
    conditions && detail?.conditionsTotalDistinct !== null && detail?.conditionsTotalDistinct !== undefined
      ? Math.max(0, detail.conditionsTotalDistinct - conditionsShown.length)
      : 0

  const target = detail?.target?.[0] ?? null
  const action = target ? actionWord(target.action) : null

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={`${card.name} quick view`}
      data-drug-quick-view={assetId ?? undefined}
    >
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Close drug overview"
        onClick={onClose}
        tabIndex={-1}
      />
      <div className={styles.panel} ref={panelRef} tabIndex={-1}>
        {/* 1: header. */}
        <div className={styles.head}>
          <p className={styles.headTitle}>Drug overview</p>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            aria-label="Close drug overview"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* 2: identity. The name is the canonical record's once it lands;
            the sphere is the card's own URI from the same name key. */}
        <div className={styles.identity}>
          <span
            className={styles.orb}
            style={{ backgroundImage: `url("${avatar.src}")` }}
            data-identity={avatar.identity}
            role="img"
            aria-label={`${card.name} sphere`}
          />
          <div className={styles.identityText}>
            <h2 className={styles.name}>{detail?.asset.canonicalName ?? card.name}</h2>
            {state.status === "loading" ? (
              <span className={styles.skelBar} style={{ width: 200 }} aria-hidden="true" />
            ) : state.status === "gated" ? null : (
              <p className={styles.aliases}>{aliasLine ?? "Names not loaded"}</p>
            )}
            <p className={styles.tracking}>✓ Tracking in {watchName}</p>
          </div>
        </div>

        {state.status === "error" ? (
          <p className={styles.error} role="alert">
            {errorCopy(state.code)}
          </p>
        ) : null}

        {/* Phase GATE: the route withheld the detail, so blocks 3 to 7 give
            way to the same tease card the Changes page shows. */}
        {state.status === "gated" ? (
          <ExpiredUpgradeCard line="Every drug you track, connected to its trials, indications and companies. Members only now." />
        ) : (
        <>
        {/* 3: stat trio, in the drawer's order (trials, names, licences). */}
        <div className={styles.statTrio} aria-busy={state.status === "loading"}>
          <StatCell
            loading={state.status === "loading"}
            value={detail ? (detail.trialCounts?.total ?? null) : null}
            label="Linked trials"
          />
          <StatCell
            loading={state.status === "loading"}
            value={detail ? (detail.identity?.acceptedNameCount ?? null) : null}
            label="Accepted names"
          />
          <StatCell
            loading={state.status === "loading"}
            value={detail ? (detail.licences ? licencePairCount(detail.licences) : null) : null}
            label="Licence matches"
          />
        </div>

        {/* 4: identity & target. */}
        <section className={styles.section} aria-labelledby="dqv-identity">
          <h3 id="dqv-identity" className={styles.heading}>
            Identity &amp; target
          </h3>
          <div className={styles.identityPanel}>
            {state.status === "loading" ? (
              <>
                <span className={styles.skelBar} style={{ width: 140 }} aria-hidden="true" />
                <span className={styles.skelBar} style={{ width: 260 }} aria-hidden="true" />
                <span className={styles.skelBar} style={{ width: 180 }} aria-hidden="true" />
              </>
            ) : detail ? (
              <>
                {classRows.length === 0 ? (
                  <div className={styles.badgeRow}>
                    <SourceBadge
                      muted
                      value={detail.classes === null ? "Class not loaded" : "No class recorded"}
                    />
                  </div>
                ) : null}
                {classRows.map((r) => {
                  const { label, source } = splitAxisLabel(r.axis, r.first.axisLabel)
                  return (
                    <div key={r.axis} className={styles.badgeRow}>
                      <SourceBadge
                        label={label}
                        source={source}
                        value={r.first.name}
                        extra={r.extra > 0 ? `+${num(r.extra)} more` : undefined}
                      />
                    </div>
                  )
                })}
                <div className={styles.badgeRow}>
                  {target ? (
                    <SourceBadge
                      label="Target"
                      source="Open Targets"
                      value={action ? `${target.name} · ${action}` : target.name}
                    />
                  ) : (
                    <SourceBadge
                      muted
                      value={detail.target === null ? "Target not loaded" : "No target named"}
                    />
                  )}
                </div>
                <div className={styles.badgeRow}>
                  {identifierBadges && identifierBadges.length > 0 ? (
                    identifierBadges.map((b) => (
                      <SourceBadge key={b.key} label={b.label} value={b.value} />
                    ))
                  ) : (
                    <SourceBadge
                      muted
                      value={
                        identifierBadges === null ? "Identifiers not loaded" : "No identifiers recorded"
                      }
                    />
                  )}
                </div>
                <div className={styles.badgeRow}>
                  {holder ? (
                    <SourceBadge label="Holder" value={holderDisplay(holder)} />
                  ) : (
                    <SourceBadge
                      label="Holder"
                      muted
                      value={detail.licences === null ? "not loaded" : "not recorded"}
                    />
                  )}
                </div>
              </>
            ) : null}
          </div>
        </section>

        {/* 5: licensed uses. "View evidence" is hidden until 4b. */}
        <section className={styles.section} aria-labelledby="dqv-uses">
          <div className={styles.sectionHead}>
            <h3 id="dqv-uses" className={styles.heading}>
              Licensed uses
            </h3>
          </div>
          {state.status === "loading" ? (
            <div className={styles.chipWrap} aria-hidden="true">
              <span className={styles.skelBar} style={{ width: 110, height: 34 }} />
              <span className={styles.skelBar} style={{ width: 90, height: 34 }} />
              <span className={styles.skelBar} style={{ width: 150, height: 34 }} />
            </div>
          ) : uses === null ? (
            <p className={styles.emptyRow}>Licences not loaded</p>
          ) : uses.length === 0 ? (
            <p className={styles.emptyRow}>No indication on record</p>
          ) : (
            <div className={styles.chipWrap}>
              {uses.map((u) => (
                <span key={u.meshId} className={styles.chip}>
                  {u.name}
                </span>
              ))}
            </div>
          )}
          {showCaveat ? <p className={styles.caveat}>{LICENCE_CAVEAT}</p> : null}
        </section>

        {/* 6: conditions being studied, with the undrawn "+N more". */}
        <section
          className={`${styles.section} ${styles.conditions}`}
          aria-labelledby="dqv-conditions"
        >
          <h3 id="dqv-conditions" className={styles.heading}>
            Conditions being studied
          </h3>
          {state.status === "loading" ? (
            <>
              <span className={styles.skelBar} style={{ width: "70%" }} aria-hidden="true" />
              <span className={styles.skelBar} style={{ width: "55%" }} aria-hidden="true" />
              <span className={styles.skelBar} style={{ width: "60%" }} aria-hidden="true" />
            </>
          ) : conditions === null ? (
            <p className={styles.emptyRow}>Conditions not loaded</p>
          ) : conditions.length === 0 ? (
            <p className={styles.emptyRow}>No conditions on record</p>
          ) : (
            <>
              {conditionsShown.map((c) => (
                <p key={c.meshId} className={styles.condRow}>
                  <span className={styles.condName} title={c.name}>
                    {c.name}
                  </span>
                  <span className={styles.condCount}>
                    {num(c.trials)} {c.trials === 1 ? "trial" : "trials"}
                  </span>
                </p>
              ))}
              {!showAllConditions && moreConditions > 0 ? (
                <button
                  type="button"
                  className={styles.more}
                  onClick={() => setShowAllConditions(true)}
                >
                  +{num(moreConditions)} more
                </button>
              ) : showAllConditions && moreConditions > 0 ? (
                /* The composer caps the list; the total is still honest. */
                <p className={styles.emptyRow}>
                  Showing {num(conditions.length)} of{" "}
                  {num(detail?.conditionsTotalDistinct ?? conditions.length)} conditions
                </p>
              ) : null}
            </>
          )}
        </section>

        {/* 7: follow the evidence, as drawn (text, not links). */}
        <div className={styles.follow}>
          <p className={styles.followHeading}>Follow the evidence</p>
          <p className={styles.followBody}>{FOLLOW_BODY}</p>
        </div>
        </>
        )}

        {/* 8: "Open full drug record" is HIDDEN until Phase 4b lands the
            record page at /tools/trial-tracker/drugs/[id]; its QDG-0 values
            (space-between, padding 17 20, radius 12, #17181B, 15/22 white)
            are in port-spec-round1.md §4.2 and land with that phase. */}
      </div>
    </div>
  )
}

function StatCell({
  loading,
  value,
  label,
}: {
  loading: boolean
  value: number | null
  label: string
}) {
  return (
    <div className={styles.statCell}>
      {loading ? (
        <span className={styles.skelBar} style={{ width: 48, height: 30 }} aria-hidden="true" />
      ) : value === null ? (
        <p className={styles.statValueNone}>Not loaded</p>
      ) : (
        <p className={styles.statValue}>{num(value)}</p>
      )}
      <p className={styles.statLabel}>{label}</p>
    </div>
  )
}
